import { useEffect, useState } from "react";
import { MonitorDown, Copy, Download, Terminal, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import DevicesPanel from "@/components/admin/DevicesPanel";

const API = process.env.REACT_APP_BACKEND_URL;
const card = "rounded-xl bg-white border border-border p-5 card-soft";

export default function RemoteAppTab() {
  const { t } = useApp();
  const [autoApp, setAutoApp] = useState(true);
  const [autoDisplay, setAutoDisplay] = useState(false);
  const [kiosk, setKiosk] = useState(false);
  const [shops, setShops] = useState([]);
  const [shop, setShop] = useState("");
  const [code, setCode] = useState("");
  const mk = () => api.post("/admin/desktop/link", { autostart: autoApp, autostart_display: autoDisplay, kiosk, shop: shop || null }).then((r) => setCode(r.data.code)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { api.get("/admin/stats").then((r) => setShops(r.data.shops || [])).catch(() => {}); }, []);
  useEffect(() => { setCode(""); const h = setTimeout(mk, 300); return () => clearTimeout(h); }, [autoApp, autoDisplay, kiosk, shop]); // eslint-disable-line
  const gen = mk;
  const host = API.replace(/^https?:\/\//, "");
  const token = code;
  const base = `${API}/api/d/${code}`;
  const cmd = `cmd /k curl -sL -o %TEMP%\\o.cmd https://${host}/api/d/${code}/OrbitPMS-Setup.bat && %TEMP%\\o.cmd`;
  const copy = () => navigator.clipboard?.writeText(cmd).then(() => toast.success(t("copied"))).catch(() => toast.error(t("copy_fail")));
  const Opt = ({ label, v, set, id }) => <label className="flex items-center justify-between gap-2 rounded-lg bg-secondary p-3 text-sm"><span className="font-semibold">{label}</span><Switch checked={v} onCheckedChange={set} data-testid={id} /></label>;
  return (
    <div className="space-y-5" data-testid="admin-remoteapp-tab">
      <div className={`${card} space-y-3`}>
        <h3 className="font-heading text-lg flex items-center gap-2"><MonitorDown className="w-5 h-5" />{t("ra_title")}</h3>
        <p className="text-sm text-muted-foreground">{t("ra_desc")}</p>
        <label className="block space-y-1" data-testid="ra-shop-field">
          <span className="text-sm font-bold">{t("ra_shop")}</span>
          <select value={shop} onChange={(e) => setShop(e.target.value)} className="w-full h-11 rounded-xl border border-border bg-white px-3 text-sm" data-testid="ra-shop-select">
            <option value="">{t("ra_shop_any")}</option>
            {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <span className="block text-xs text-muted-foreground">{t("ra_shop_hint")}</span>
        </label>
        <div className="grid sm:grid-cols-3 gap-2">
          <Opt label={t("autostart_app")} v={autoApp} set={setAutoApp} id="ra-opt-autostart" />
          <Opt label={t("autostart_display")} v={autoDisplay} set={setAutoDisplay} id="ra-opt-display" />
          <Opt label={t("autostart_kiosk")} v={kiosk} set={setKiosk} id="ra-opt-kiosk" />
        </div>
      </div>
      <div className="rounded-xl bg-[#0B2A5B] text-white p-4 space-y-2" data-testid="ra-oneline">
        <div className="text-sm font-bold flex items-center gap-2"><Terminal className="w-4 h-4 text-[#F28C28]" />{t("oneline_title")}</div>
        <p className="text-xs opacity-80">{t("ra_cmd_hint")}</p>
        <div className="flex gap-2 items-stretch">
          <code className="flex-1 min-w-0 rounded-lg bg-black/40 px-3 py-2 text-[11px] break-all font-mono select-all" dir="ltr" data-testid="ra-cmd">{token ? cmd : "…"}</code>
          <button type="button" onClick={copy} disabled={!token} className="px-3 rounded-lg bg-[#F28C28] text-[#1A1A1A] text-xs font-bold flex items-center gap-1 shrink-0 disabled:opacity-50" data-testid="ra-copy-btn"><Copy className="w-4 h-4" />{t("copy")}</button>
        </div>
        <ol className="text-[11px] opacity-90 space-y-0.5 list-decimal ps-4"><li>{t("oneline_s1")}</li><li>{t("oneline_s2")}</li><li>{t("oneline_s3")}</li></ol>
      </div>
      <div className={`${card} space-y-3`}>
        <div className="text-sm font-bold">{t("remoteapp_or_file")}</div>
        <div className="flex flex-wrap gap-2">
          <a href={token ? `${base}/OrbitPMS-Setup.bat` : undefined} download className="h-10 px-4 rounded-xl bg-[#1F4E8C] text-white text-sm font-bold flex items-center gap-2" data-testid="ra-win-btn"><Download className="w-4 h-4" />{t("remoteapp_win")}</a>
          <a href={token ? `${base}/OrbitPMS-Setup.command` : undefined} download className="h-10 px-4 rounded-xl border border-border bg-white text-sm font-bold flex items-center gap-2" data-testid="ra-mac-btn"><Download className="w-4 h-4" />{t("remoteapp_mac")}</a>
          <button type="button" onClick={gen} className="h-10 px-4 rounded-xl border border-border bg-white text-sm font-bold flex items-center gap-2" data-testid="ra-regen-btn"><RefreshCw className="w-4 h-4" />{t("ra_regen")}</button>
        </div>
        <p className="text-xs text-muted-foreground">{t("ra_token_hint")}</p>
        <ul className="text-xs text-muted-foreground list-disc ps-5 space-y-0.5"><li>{t("install_feat1")}</li><li>{t("ra_feat_lock")}</li><li>{t("install_feat3")}</li></ul>
      </div>
      <DevicesPanel />
    </div>
  );
}
